import { useEffect, useRef, useState } from "react";
import { Progress } from "@/components/ui/progress";
import {
  isJobTerminal,
  statusLabel,
  type LinkedInJob,
  type LinkedInJobStatus,
} from "@/types/linkedin";

// Bandas sintéticas de progresso por fase [piso, teto]. O valor sobe aos poucos
// dentro da banda enquanto a fase dura (percepção de progresso) e é monotônico
// entre fases (nunca regride). `enriching` (a chamada de IA) é a fase longa —
// vai até ~90; matching/diffing são rápidas e levam de 90 a ~99, então
// review_ready fecha em 100.
const BANDS: Partial<Record<LinkedInJobStatus, [number, number]>> = {
  pending: [6, 10],
  uploading: [12, 22],
  extracting: [25, 45],
  enriching: [45, 90],
  matching: [90, 95],
  diffing: [95, 99],
};

// Constante de tempo (ms) da curva assintótica de cada fase. Maior = sobe mais
// devagar. enriching é deliberadamente lento pra durar ~os segundos da IA.
const TAU: Partial<Record<LinkedInJobStatus, number>> = {
  extracting: 4000,
  enriching: 16000,
  matching: 2000,
  diffing: 2000,
};

// Tempo (ms) em cada sub-passo antes de avançar pro próximo. Os sub-passos
// avançam 1 a 1 e PARAM no último (sem repetir o ciclo — repetir parece falso).
const STEP_MS = 6000;

// Textos de sub-passo que avançam em sequência durante a fase, dando a sensação
// de várias etapas internas mesmo quando o backend só reporta um status.
const SUBSTEPS: Partial<Record<LinkedInJobStatus, string[]>> = {
  extracting: ["Lendo o PDF…", "Extraindo o texto…"],
  enriching: [
    "Lendo experiências e cargos…",
    "Inferindo áreas de atuação…",
    "Analisando o padrão de carreira…",
    "Estruturando o resultado…",
  ],
  matching: ["Procurando o freelancer…"],
  diffing: ["Comparando com a versão anterior…"],
};

export function JobProgressCell({ job }: { job: LinkedInJob }) {
  const terminal = isJobTerminal(job.status);

  // Marca o instante (relógio do cliente) em que a fase atual começou — evita
  // depender do updated_at do servidor (skew de relógio). Atualizar a ref no
  // corpo do render é idempotente: só muda quando o status muda.
  const phaseRef = useRef<{ status: string; since: number }>({
    status: job.status,
    since: Date.now(),
  });
  if (phaseRef.current.status !== job.status) {
    phaseRef.current = { status: job.status, since: Date.now() };
  }

  // Tick local pra animar a barra entre os polls (a query refaz a cada 2s).
  const [, setTick] = useState(0);
  useEffect(() => {
    if (terminal) return;
    const id = setInterval(() => setTick((t) => t + 1), 600);
    return () => clearInterval(id);
  }, [terminal, job.status]);

  if (terminal) {
    return (
      <span className="text-xs text-muted-foreground">
        {job.status === "failed"
          ? job.error_message?.slice(0, 80)
          : "Concluído"}
      </span>
    );
  }

  const elapsed = Date.now() - phaseRef.current.since;
  const band = BANDS[job.status];
  let pct = job.progress_pct;
  if (band) {
    const [lo, hi] = band;
    const tau = TAU[job.status] ?? 5000;
    const synthetic = lo + (hi - lo) * (1 - Math.exp(-elapsed / tau));
    pct = Math.max(job.progress_pct, Math.round(synthetic));
  }

  const subs = SUBSTEPS[job.status];
  const sub =
    subs && subs.length > 0
      ? subs[Math.min(Math.floor(elapsed / STEP_MS), subs.length - 1)]
      : statusLabel(job.status);

  return (
    <div className="space-y-1">
      <Progress value={pct} className="h-2" />
      <div className="flex justify-between gap-2 text-[11px] text-muted-foreground">
        <span className="truncate">{sub}</span>
        <span className="tabular-nums">{Math.round(pct)}%</span>
      </div>
    </div>
  );
}
